/**
 * Inspection state — active inspection target + populated payload +
 * the in-flight flag for server-round-trip cases.
 *
 * Phase 1 offline-dev: the payload is synthesized locally from the
 * world-model at click time (`derive.ts`). When SERVER's P1.5 Gateway
 * lands, the store gains a `awaitingServer: true` flag, the local
 * synthesis is kept as a placeholder, and the `InspectionPayload`
 * envelope arriving over the wire upgrades it to the canonical
 * server-sourced body.
 */

import { create } from 'zustand';

import type { InspectTarget, InspectionData } from '../wire';

export interface InspectionState {
  /** Current inspect target, if the panel is open. */
  target: InspectTarget | null;
  /** Rendered inspection body — local synthesis in Phase 1, server-
   *  sourced once P1.5 Gateway lands. Null while awaiting data. */
  data: InspectionData | null;
  /** True while a server round-trip is in flight (placeholder body
   *  shown meanwhile). Phase 1: always false. */
  awaitingServer: boolean;

  // ─── Actions ─────────────────────────────────────────────────────
  openWith: (target: InspectTarget, data: InspectionData) => void;
  setServerPayload: (target: InspectTarget, data: InspectionData) => void;
  close: () => void;
}

export const useInspection = create<InspectionState>((set) => ({
  target: null,
  data: null,
  awaitingServer: false,

  openWith: (target, data) =>
    set(() => ({ target, data, awaitingServer: false })),

  setServerPayload: (target, data) =>
    set(() => ({ target, data, awaitingServer: false })),

  close: () =>
    set(() => ({ target: null, data: null, awaitingServer: false })),
}));
